import React, { useContext, useState } from 'react';
import {
    Card, List, Button, Space, Typography, Popconfirm, Collapse, Empty,
} from 'antd';
import {
    EditOutlined, DeleteOutlined, DragOutlined, EyeOutlined,
} from '@ant-design/icons';
import { ToursContext } from '../../context/tours.context';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

const StepsList = () => {
    const {
        steps,
        startAddingStep,
        deleteStep,
        reorderSteps,
    } = useContext(ToursContext);

    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newSteps = [...steps];
        const draggedStep = newSteps[draggedIndex];

        newSteps.splice(draggedIndex, 1);
        newSteps.splice(dropIndex, 0, draggedStep);

        reorderSteps(newSteps);

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDelete = (stepId: string) => {
        deleteStep(stepId);
    };

    const truncateText = (text: string, maxLength: number = 50) => (text.length > maxLength ? `${text.substring(0, maxLength)}...` : text);

    return (
        <>
            <List
                size="small"
                dataSource={steps}
                renderItem={(step, index) => (
                    <div
                        key={step.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        style={{
                            marginBottom: 8,
                            border: dragOverIndex === index ? '2px dashed #1890ff' : '1px solid #d9d9d9',
                            borderRadius: 4,
                            transition: 'all 0.2s',
                        }}
                    >
                        <List.Item
                            style={{
                                padding: '8px 12px',
                                cursor: 'grab',
                            }}
                            actions={[
                                <Button
                                    key="edit"
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => startAddingStep(step)}
                                />,
                                <Popconfirm
                                    key="delete"
                                    title="Delete this step?"
                                    onConfirm={() => handleDelete(step.id)}
                                    okText="Delete"
                                    cancelText="Cancel"
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        danger
                                    />
                                </Popconfirm>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={(
                                    <div style={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        backgroundColor: '#1890ff',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                    }}
                                    >
                                        {index + 1}
                                    </div>
                                )}
                                title={(
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <DragOutlined style={{ color: '#ccc', fontSize: '12px' }} />
                                        <Text strong style={{ fontSize: '13px' }}>
                                            {truncateText(step.title, 30)}
                                        </Text>
                                    </div>
                                )}
                                description={(
                                    <div>
                                        {step.description && (
                                            <Paragraph
                                                style={{
                                                    margin: 0,
                                                    fontSize: '11px',
                                                    color: '#666',
                                                }}
                                            >
                                                {truncateText(step.description, 40)}
                                            </Paragraph>
                                        )}
                                        <Collapse
                                            ghost
                                            style={{ marginTop: 4 }}
                                        >
                                            <Panel
                                                header={(
                                                    <Text
                                                        type="secondary"
                                                        style={{ fontSize: '10px' }}
                                                    >
                                                        <EyeOutlined />
                                                        {' '}
                                                        View selector
                                                    </Text>
                                                )}
                                                key="1"
                                                style={{ padding: 0 }}
                                            >
                                                <Text
                                                    code
                                                    style={{
                                                        fontSize: '10px',
                                                        wordBreak: 'break-all',
                                                    }}
                                                >
                                                    {step.selector}
                                                </Text>
                                            </Panel>
                                        </Collapse>
                                    </div>
                                )}
                            />
                        </List.Item>
                    </div>
                )}
            />

            <div style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 4,
                fontSize: '13px',
                textAlign: 'center',
            }}
            >
                💡 Arrastra los steps para reordenarlos
            </div>
        </>
    );
};

export default StepsList;
